import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { SetlistRepository } from '@/api/setlists/setlist.repository';
import type { SetlistSongsSchema } from '@/api/setlists/setlist.schema';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import type { ListQuery } from '@/types/api/list-api';
import { createCrudHooks } from './create-crud-hooks';

const crud = createCrudHooks(apiClient['setlists'], 'setlists');

export const setlistKeys = crud.keys;
export const useCreateSetlist = crud.useCreate;
export const useUpdateSetlist = crud.useUpdate;
export const useDeleteSetlist = crud.useDelete;

// A listagem e o detalhe devolvem mais do que o tipo genérico do Eden descreve (contagem,
// prévia, músicas com cifra), então os tipos vêm direto do repositório.
type ListResult = Awaited<ReturnType<SetlistRepository['findAllWithStats']>>;
export type SetlistListItem = ListResult['data'][number];
export type SetlistDetail = NonNullable<
  Awaited<ReturnType<SetlistRepository['findDetail']>>
>;
export type SetlistItem = SetlistDetail['items'][number];

export const useSetlists = (query?: ListQuery<(typeof apiClient)['setlists']>) =>
  useQuery({
    queryKey: setlistKeys.list(query),
    queryFn: async () => {
      const res = await apiClient.setlists.get({ query });
      return res.data as unknown as ListResult;
    },
  });

export const useInfiniteSetlists = (
  query?: Omit<NonNullable<ListQuery<(typeof apiClient)['setlists']>>, 'page'>,
) =>
  useInfiniteQuery({
    queryKey: [...setlistKeys.all, 'infinite', query],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.setlists.get({ query: { ...query, page: pageParam } });
      return res.data as unknown as ListResult;
    },
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.totalPages
        ? last.pagination.page + 1
        : undefined,
  });

export const useSetlist = (id: string | undefined) =>
  useQuery({
    queryKey: setlistKeys.detail({ id: id ?? '' }),
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await apiClient.setlists({ id: id! }).get();
      return res.data as unknown as SetlistDetail;
    },
  });

/** Substitui a lista de músicas do repertório (ordem = posição). */
export const useReplaceSetlistSongs = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & SetlistSongsSchema) => {
      const res = await apiClient.setlists({ id }).songs.put(body);
      return res.data as unknown as SetlistDetail;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setlistKeys.all });
    },
    onError: (error: any) => {
      toast.error(error?.value?.errors?.items ?? 'Não foi possível salvar as músicas.');
    },
  });
};
